<template>
    <div>
        <div class="rounded-lg bg-white shadow sm:py-32">
            <div class="mx-auto max-w-7xl px-6 lg:px-8">
                <div class="mx-auto max-w-2xl sm:text-center">
                    <h2
                        class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                    >
                        {{ $t("vue.simple_no_tricks_pricing") }}
                    </h2>
                    <p class="mt-6 text-lg leading-8 text-gray-600">
                        {{ $t("vue.only_one_simple_plan") }}
                    </p>
                </div>

                <div
                    class="mx-auto mt-16 max-w-2xl rounded-3xl ring-1 ring-gray-200 sm:mt-20 lg:mx-0 lg:flex lg:max-w-none"
                >
                    <div class="p-8 sm:p-10 lg:flex-auto">
                        <h3
                            class="text-2xl font-bold tracking-tight text-gray-900"
                        >
                            {{ $t("vue.paid_plan") }}
                        </h3>
                        <p class="mt-6 text-base leading-7 text-gray-600">
                            {{ $t("vue.all_you_need_for_dmarc") }}
                        </p>
                        <div class="mt-10 flex items-center gap-x-4">
                            <h4
                                class="flex-none text-sm font-semibold leading-6 text-indigo-600"
                            >
                                {{ $t("vue.whats_included") }}
                            </h4>
                            <div class="h-px flex-auto bg-gray-100"></div>
                        </div>
                        <ul
                            role="list"
                            class="mt-8 grid grid-cols-1 gap-4 text-sm leading-6 text-gray-600 sm:grid-cols-2 sm:gap-6"
                        >
                            <li class="flex gap-x-3">
                                <svg
                                    class="h-6 w-5 flex-none text-indigo-600"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                {{ $t("vue.pay_per_domain") }}
                            </li>
                            <li class="flex gap-x-3">
                                <svg
                                    class="h-6 w-5 flex-none text-indigo-600"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                {{ $t("vue.unlimited_users") }}
                            </li>
                            <li class="flex gap-x-3">
                                <svg
                                    class="h-6 w-5 flex-none text-indigo-600"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                {{ $t("vue.dmarc_record_monitoring") }}
                            </li>
                            <li class="flex gap-x-3">
                                <svg
                                    class="h-6 w-5 flex-none text-indigo-600"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                {{ $t("vue.days_history") }}
                            </li>
                        </ul>
                    </div>
                    <div
                        class="-mt-2 p-2 lg:mt-0 lg:w-full lg:max-w-md lg:shrink-0"
                    >
                        <div
                            class="rounded-2xl bg-gray-50 py-10 text-center ring-1 ring-inset ring-gray-900/5 lg:flex lg:flex-col lg:justify-center lg:py-16"
                        >
                            <div class="mx-auto max-w-xs px-8">
                                <Switch
                                    v-model="annually"
                                    class="flex items-center justify-center"
                                    :left-label="$t('vue.monthly')"
                                    :right-label="$t('vue.yearly')"
                                />
                                <p
                                    class="mt-8 flex items-baseline justify-center gap-x-2"
                                >
                                    <span
                                        class="text-5xl font-bold tracking-tight text-gray-900"
                                        >${{
                                            selectedPrice?.totalPrice
                                        }}
                                        /</span
                                    >
                                    <span
                                        class="text-sm font-semibold leading-6 tracking-wide text-gray-600"
                                        >{{ $t("vue.domain") }}</span
                                    >
                                </p>
                                <button
                                    class="mt-10 block w-full cursor-pointer rounded-md bg-orange-500 px-3 py-2 text-center text-sm font-semibold text-gray-700 shadow-sm hover:text-gray-800 focus:text-gray-800 focus:ring-orange-500"
                                    @click="subscriptionModalOpened = true"
                                >
                                    {{ $t("vue.subscribe") }}
                                </button>
                                <p class="mt-6 text-xs leading-5 text-gray-600">
                                    {{ $t("vue.invoices_receipts_available") }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <SubscriptionBuyModal
            v-if="subscriptionModalOpened && selectedPrice"
            :paddle-client-token="props.paddleClientToken"
            :paddle-price-id="selectedPrice?.priceId"
            :paddle-environment="props.paddleEnvironment"
            :domains-count="props.domainsCount"
            :period="annually ? 'annually' : 'monthly'"
            @close="handleCloseSubscriptionModal"
        />
    </div>
</template>

<script lang="ts" setup>
const subscriptionModalOpened = ref(false);
const props = defineProps({
    domainsCount: {
        type: Number,
        default: () => 0,
    },
    paddleClientToken: {
        type: String,
        default: "",
    },
    selectedPlan: {
        type: Array as () => {
            type: "monthly" | "yearly";
            priceId: string;
            productId: string;
            totalPrice?: number;
        }[],
        default: () => [],
    },
    paddleEnvironment: {
        type: String as () => "production" | "sandbox",
        default: "production",
    },
});

const selectedPrice = computed(() => {
    const priceId = props.selectedPlan.find(
        (price) => price.type === (yearly.value ? "yearly" : "monthly"),
    );
    return priceId;
});
const yearly = ref(true);

const handleCloseSubscriptionModal = () => {
    subscriptionModalOpened.value = false;

    router.reload();
};
</script>
